import {ReactElement, useEffect, useState} from "react";
import {GetLeaseExpirationCountsForRewriteQuery, useGetLeaseExpirationCountsForRewriteQuery, useMassUpdateLeaseExpirationCountsForPropertyAndPeriodMutation, VersionType} from "../../../../__generated__/generated_types";
import {toast} from "react-toastify";
import {TUnitTypeData, UnitTypeSyncWithPMSTable} from "../UnitTypeSyncWithPMSTable";


interface LeaseExpirationsRewriteViewProps {
    propertyId: string,
    valuesStartFromIndex: number,
    versionType: VersionType,
    onCancel: () => void,
    onApply: () => void;
    budgetYear: number;
}

function buildUnitTypesData(rawData: GetLeaseExpirationCountsForRewriteQuery, valuesStartFromIndex: number): TUnitTypeData[] {
    const forecastedDataByUnitTypeId = rawData.queryForecastedLeaseExpirationCountsForPropertyAndPeriod.toIdMap("unitTypeId");
    const ret = [] as TUnitTypeData[];
    for(const pmsDataRow of rawData.queryPMSLeaseExpirationCountsForPropertyAndPeriod) {
        const forecastedDataRow = forecastedDataByUnitTypeId[pmsDataRow.unitTypeId];
        const forecastedValues = forecastedDataRow ? forecastedDataRow.values : new Array(12).fill(null);
        ret.push({
            unitTypeId: pmsDataRow.unitTypeId,
            unitTypeName: pmsDataRow.unitTypeName,
            pmsValues: pmsDataRow.values.map((v, i) => i >= valuesStartFromIndex ? v : null),
            currentForecastedValues: forecastedValues.map((v, i) => i >= valuesStartFromIndex ? v : null),
            newForecastedValues: pmsDataRow.values.map((v, i) => i >= valuesStartFromIndex ? v : null),
            useNewForecasedValues: true
        });
    }

    return ret;
}

export function LeaseExpirationsRewriteView(props: LeaseExpirationsRewriteViewProps): ReactElement {
    const {data: expirationCountsRawData, loading: expirationCountsRawLoading} = useGetLeaseExpirationCountsForRewriteQuery({
        variables: {
            propertyId: props.propertyId,
            versionType: props.versionType,
            budgetYear: props.budgetYear,
        },
        fetchPolicy: "no-cache"
    });
    const [saveChanges, {loading: isSavingChanges}] = useMassUpdateLeaseExpirationCountsForPropertyAndPeriodMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if(data?.massUpdateLeaseExpirationCountsForPropertyAndPeriod === true) {
                toast.success(`Updated Successfully`);
            }
            else {
                toast.error(`Failed To Update`);
            }
            props.onApply();
        },
    });

    const [unitTypesData, setUnitTypesData] = useState<TUnitTypeData[]>();

    useEffect(() => {
        if(!expirationCountsRawData || expirationCountsRawLoading) {
            return;
        }

        const unitTypesData = buildUnitTypesData(expirationCountsRawData, props.valuesStartFromIndex);
        setUnitTypesData(unitTypesData.sortBy("unitTypeName"));
    }, [expirationCountsRawData, expirationCountsRawLoading]);

    function handleApply(updates: {
        unitTypeId: string;
        values: (number | null)[];
    }[]) {
        saveChanges({
            variables: {
                propertyId: props.propertyId,
                versionType: props.versionType,
                updates: updates,
                budgetYear: props.budgetYear
            }
        });
    }

    return (
        <UnitTypeSyncWithPMSTable
            data={unitTypesData}
            valuesStartFromIndex={props.valuesStartFromIndex}
            isApplying={isSavingChanges}
            onCancel={props.onCancel}
            onApply={handleApply}
        />
    );
}